import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Always try to check auth with server
      // The session cookie (if it exists) will be sent automatically by the browser
      const response = await api.get("/auth/me");

      if (response?.success && response?.user) {
        setUser(response.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      // 401 error means no valid session
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (identifier, password, method = "email") => {
    const loginData =
      method === "email"
        ? { email: identifier, password }
        : { cid: identifier, password };

    const response = await api.post("/auth/login", loginData);
    setUser(response.user);
    return response;
  }, []);

  const signup = useCallback(async (userData) => {
    const response = await api.post("/auth/signup", userData);
    setUser(response.user);
    return response;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      window.location.href = "/login";
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      signup,
      logout,
      checkAuth,
    }),
    [user, loading, login, signup, logout, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
